import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Calculator from '@components/Utilities/Calculator';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import CashRegisterInfo from '@components/SalesReport/Modals/CashRegisterInfo';
import Authorization from '@components/Utilities/Authorization';
import { useDispatch } from 'react-redux';
import TooltipButton from '@components/Utilities/Buttons/TooltipButton';
import { setActivityDrawer } from '@store/appStore';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { setCashRegister } from 'src/shared/store/krispisStore';
import { CashRegister } from '@api/CashRegister/CashRegister';

interface ModalCashRegisterProps {
    modalCashRegister: OpenModalHookType;
    cashRegister: CashRegister;
}

export const ModalCashRegister: React.FunctionComponent<ModalCashRegisterProps> = (props: any) => {
    const { modalCashRegister, cashRegister } = props;
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const [amount, setAmount] = useState<number>(0);
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const dispatch = useDispatch();

    const handleClose = () => {
        setAmount(0);
        modalCashRegister.toggle();
    };

    const onOpenCash = async () => {
        showLoader();
        try {
            let cashRegisterData;
            if (cashRegister?.id) {
                cashRegisterData = await cashRegisterService.updateInitialAmount(cashRegister.id, amount);
            } else {
                cashRegisterData = await cashRegisterService.openCashRegister(amount);
            }
            dispatch(setCashRegister(cashRegisterData));
            handleClose();
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        if (modalCashRegister.modalOpen) {
            setIsAuthorized(false);
        }
    }, [modalCashRegister.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 25, padding: 4 }
            }}
            open={modalCashRegister.modalOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={600}>
                        Caja inicial
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <TooltipButton
                            onClick={() =>
                                dispatch(
                                    setActivityDrawer({
                                        isOpen: true,
                                        section: 'cashRegister',
                                        sectionId: cashRegister?.id
                                    })
                                )
                            }
                            tooltip="Mostrar actividad"
                            icon={<PriorityHighIcon />}
                        />
                        <IconButton color="secondary" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {!cashRegister && (
                        <Stack alignItems="center" spacing={2}>
                            <Typography>La caja aun no a sido abierta</Typography>
                        </Stack>
                    )}
                    {cashRegister && <CashRegisterInfo cashRegister={cashRegister} />}
                    <Calculator disableKeyPress={!!cashRegister} onChangeValue={setAmount} />
                    {cashRegister && (
                        <Authorization
                            onAuthorized={() => setIsAuthorized(true)}
                            onUnauthorized={() => setIsAuthorized(false)}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={!isAuthorized} fullWidth color="success" variant="contained" onClick={onOpenCash}>
                    {!cashRegister ? 'Abrir caja' : 'Actualizar monto inicial en caja'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
